import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import produce from "immer";

import "./UpdateFiles.scss";
import { ListUpdateFile } from "./ListUpdateFile";
import { ErrorBoundary, ErrorMessage, Page, PageHeader, Loader } from "components";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, IconButton} from '@cfa/react-components';
import { AsyncStatus } from "enums";
import { IUpdateFile, IUpdateType } from "interfaces";
import { ManufacturersService, parseAxiosError, UpdateFilesService, UpdateTypesService } from "services";

interface IUpdateFilePageState {
    loadStatus: AsyncStatus;
    loadError?: string;
    deleteStatus?: AsyncStatus;
    deleteError?: string;
    updateFiles?: IUpdateFile[];
    filteredUpdateFiles?: IUpdateFile[];
    manufacturerId?: number;
    updateTypeId?: number;
    deleteUpdateFile?: IUpdateFile;
}

export function UpdateFiles() {

    const [pageState, setPageState] = useState<IUpdateFilePageState>({
        loadStatus: AsyncStatus.Success,
        deleteStatus: AsyncStatus.Initial,
        updateFiles: UpdateFilesService.allUpdateFiles,
        filteredUpdateFiles: UpdateFilesService.allUpdateFiles,
    });

    const { loadStatus, loadError, deleteStatus, deleteError, updateFiles, filteredUpdateFiles, manufacturerId, deleteUpdateFile } = pageState;
    // Modal control
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const showDeleteUpdateFileModal = (updateFile) => {
        setPageState(
            produce((draft) => {
                draft.deleteUpdateFile = updateFile;
            })
        );
        setShow(true);
    };

    // Compute derived data. We're populating updateTypes based on manufacturerId chosen.
    let updateTypes: IUpdateType[] = [];
    if (manufacturerId) {
        updateTypes = UpdateTypesService.updateTypes.filter(
            (ut) => ut.manufacturerId === manufacturerId
        );
    }

    function onChangeManufacturer(e: ChangeEvent<HTMLSelectElement>) {
        const val = e.target.value;
        const manId = val && parseInt(val, 10);
        const filter = manId ? updateFiles.filter(uf => uf.manufacturerId === manId) : updateFiles;

        setPageState(
            produce((draft) => {
                draft.manufacturerId = manId;
                draft.updateTypeId = undefined;
                draft.filteredUpdateFiles = filter;
            })
        );
    }

    // filters the list by the manufacturerId and updateTypeId
    function onChangeUpdateType(e: ChangeEvent<HTMLSelectElement>) {
        // manufacturerId is the first level of filtering, if null, don't apply updateType filter
        if (!manufacturerId) return;
        const val = e.target.value;
        const typeId = val && parseInt(val, 10);
        const filter = typeId ? 
            updateFiles.filter(uf => uf.manufacturerId === manufacturerId && uf.updateTypeId === typeId) :
            updateFiles.filter(uf => uf.manufacturerId === manufacturerId);

        setPageState(
            produce((draft) => {
                draft.updateTypeId = typeId;
                draft.filteredUpdateFiles = filter;
            })
        );
    }
 
    // Soft deletes the updateFile in the DB and removes it from the DOM
    function onDeleteUpdateFile(updateFileId: number) {
        setPageState(
            produce((draft) => {
                draft.deleteError = '';
                draft.deleteStatus = AsyncStatus.Loading;
            })
        )
        let p: Promise<void> = UpdateFilesService.softDeleteUpdateFile(updateFileId);
        p.then(() => {
            const filter = updateFiles.filter(uf => uf.id !== updateFileId);
            // We need to remove the updateFile from both the filtered view and the updateFiles collection
            setPageState(
                produce((draft) => {
                    draft.filteredUpdateFiles = filter;
                    draft.updateFiles = filter;
                    draft.deleteUpdateFile = undefined;
                    draft.deleteStatus = AsyncStatus.Success;
                })
            )
            setShow(false);
        })
        p.catch((err) => {
            setPageState(
                produce((draft) => {
                    draft.deleteError = parseAxiosError(err);
                    draft.deleteStatus = AsyncStatus.Error;
                })
            );
        });
        
    }

    const history = useHistory();

    return (<>
        <Page>

            <div className="updateFiles_header">
                <div className="updateFiles_filter">
                    <select onChange={onChangeManufacturer} defaultValue="default">
                        <option value="default" disabled hidden>
                            Manufacturer
                        </option>
                        <option value={undefined}>-----</option>
                        {ManufacturersService.manufacturers.map((m) => (
                            <option value={m.id} key={m.id}>
                                {m.name}
                            </option>
                        ))}
                    </select>
                    <select onChange={onChangeUpdateType} defaultValue="default">
                        <option value="default" disabled hidden>
                            Update Type
                        </option>
                        <option value={undefined}>-----</option>
                        {updateTypes &&
                            updateTypes.map((ut) => (
                                <option value={ut.id} key={ut.id}>
                                    {ut.description}
                                </option>
                            ))}
                    </select>
                </div>
                <Button onClick={() => history.push("/updatefiles/new")}>
                    Add Update File
                </Button>
            </div>

            <div className="row updateFiles_colHeaders">
                <div className="updateFiles_colHeader updateFiles_colHeadersMain">
                    Update File
                </div>
                <div className="updateFiles_colHeader updateFiles_colHeadersCreatedAt">
                    Created At
                </div>
                <div className="updateFiles_colHeader updateFiles_colHeadersAction"></div>
            </div>

            <div className="updateFiles_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success &&
                        filteredUpdateFiles.length > 0 &&
                        filteredUpdateFiles.map((uf) => {
                            return (
                                <ListUpdateFile
                                    key={uf.id}
                                    updateFile={uf}
                                    onDeleteUpdateFile={(updateFileId) => showDeleteUpdateFileModal(updateFileId)}
                                />
                            );
                        })}
                    {loadStatus === AsyncStatus.Success &&
                        filteredUpdateFiles.length === 0 && (
                            <div className="updateFiles_listEmpty">
                                <FontAwesomeIcon
                                    icon="search"
                                    className="updateFiles_listEmptyIcon"
                                />
                                <div className="updateFiles_listEmptyText">
                                    No update files found.
                                </div>
                            </div>
                        )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>
        </Page>

        <Modal show={show} onClose={handleClose}>
            <ModalHeader>
                Delete Update File
            </ModalHeader>
            <ModalBody>
                <p>Are you sure you want to delete this update file?</p>
                <div className="updateFiles_deleteHeader">
                    {`${deleteUpdateFile
                        && ManufacturersService.manufacturersMap[deleteUpdateFile.manufacturerId]?.name} - 
                        ${deleteUpdateFile
                            && UpdateTypesService.updateTypesMap[deleteUpdateFile.updateTypeId]?.description}`}
                </div>
                <p><b>Description: </b>{deleteUpdateFile && deleteUpdateFile.description}</p>
            </ModalBody>
            <ModalFooter>
                {deleteStatus === AsyncStatus.Error && (
                    <ErrorMessage message={deleteError} />
                )}
                <Button variant="outlined" disabled={deleteStatus === AsyncStatus.Loading} onClick={handleClose}>
                    Cancel
                </Button>
                <Button loading={deleteStatus === AsyncStatus.Loading} onClick={() => onDeleteUpdateFile(deleteUpdateFile.id)}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>
    </>);
}
